import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import cx from "classnames";

import Modal, { ModalBody, ModalFooter } from "Components/Modal";
import { LinearProgress, IconButton, InputBase, Grid, Typography } from "@mui/material";
import { NearMeOutlined } from "@mui/icons-material";

import { setChatUserWriting, chatListUpdateLastMessage, resetChatThread } from "Modules/chat/.globals/redux/actions";
import { close as closeChatsContainer, setIsExpanded as setChatsContainerIsExpanded } from "Modules/chat/views/chatsContainer/redux/slice";
import useChatContainerHeight from "Modules/chat/hooks/useChatContainerHeight";

import MessageBubble from "./components/MessageBubble";
import SkeletonChatMessages from "./components/SkeletonChatMessages";

import "Modules/chat/index.scss";
import {
  fetchChatMessages
} from "Modules/chat/.globals/redux/actions";

const DATE_LABELS = {
  sameDay: "[Today]",
  nextWeek: "dddd",
  lastDay: "[Yesterday]",
  lastWeek: "[Last] dddd",
  sameElse: "D MMMM YYYY",
};

const INPUT_MAX_LENGTH = 300;

// Height constants for container calculation
const HEADER_HEIGHT = 64;
const FOOTER_BASE_HEIGHT = 100;
const MARGIN_BUFFER = 0;
const INPUT_WITH_COUNTER_BUFFER = 50;
const INPUT_WITHOUT_COUNTER_BUFFER = 34;

const ChatThread = ({
  channel = null,
  items = [],
  onCloseThread,
  onSendMessage,
  onUpdateMessageStatus,
  openModal,
  isItemsLoading,
  nextToken,
}) => {
  const { messages, locale } = useIntl();
  const dispatch = useDispatch();

  const [draft, setDraft] = useState("");
  const [itemsGrouped, setItemsGrouped] = useState({});
  const [isNewChat, setIsNewChat] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [chatStartedLabel, setChatStartedLabel] = useState("");
  const [dateLocaleLabel, setDateLocaleLabel] = useState(DATE_LABELS);

  // Use shared height calculation hook
  const { inputRef, scrollRef, recalculate, showInputCharacterCounter } = useChatContainerHeight({
    headerHeight: HEADER_HEIGHT,
    footerBaseHeight: FOOTER_BASE_HEIGHT,
    paddingBuffer: MARGIN_BUFFER,
    inputCounterVisibleHeight: INPUT_WITH_COUNTER_BUFFER,
    inputCounterHiddenHeight: INPUT_WITHOUT_COUNTER_BUFFER,
    minInputHeight: 15,
    enabled: openModal,
  });
  const [oldScrollTop, setOldScrollTop] = useState(0); // the scroll top when load trigger

  const { authId } = useSelector((state) => state.securityRedux);
  const chatUsers = useSelector((state) => state.catalogsRedux.chatUsers);
  const { loading = false } = useSelector((state) => state.chatThreadRedux || {});
  const { isExpanded } = useSelector((state) => state.chatsContainerRedux);

  const hasMessages = Array.isArray(items) && items.length > 0;
  const showInitialSkeleton = isItemsLoading && !hasMessages;
  const activeChat = channel || { id: "", type: "team", users: "[]", name: null };
  const chatType = activeChat.type || "team";

  // Memoize parsed user IDs to avoid JSON.parse on every render
  const chatUserIds = useMemo(() => {
    if (!activeChat?.users) return [];
    try {
      return JSON.parse(activeChat.users);
    } catch {
      return [];
    }
  }, [activeChat?.users]);

  const chatUserId = useMemo(() =>
    chatUserIds.find((userId) => userId !== authId),
    [chatUserIds, authId]
  );

  const activePartner = useMemo(() =>
    chatUsers?.find((user) => user.authId === chatUserId),
    [chatUsers, chatUserId]
  );

  // Memoize chat user lookup
  const chatUser = useMemo(() =>
    chatUsers?.find(x => x.authId === chatUserId),
    [chatUsers, chatUserId]
  );

  // Memoize chat user status calculation
  const chatUserStatus = useMemo(() => {
    if (!chatUser?.lastConnected)
      return messages["chat_offlineStatus"];

    if (moment().diff(moment(chatUser.lastConnected), 'minute') < 3)
      return messages["chat_onlineStatus"];

    if (chatUser.lastWriting) {
      const writingStatusSplit = chatUser.lastWriting.split('|');
      if (writingStatusSplit[0] === channel?.id &&
        moment().diff(moment(writingStatusSplit[1]), 'second') < 30) {
        return messages["chat_writingStatus"];
      }
    }

    return messages["chat_offlineStatus"];
  }, [chatUser, channel?.id, messages]);

  // Memoize fetchMoreMessages to prevent recreation
  const fetchMoreMessages = useCallback(() => {
    if (nextToken && channel?.id && !isLoadingHistory) {
      setOldScrollTop(scrollRef?.current?.scrollTop || 0);
      setIsLoadingHistory(true);
      dispatch(fetchChatMessages({
        chatId: channel.id,
        limit: 10,
        nextToken,
      }));
    }
  }, [nextToken, channel?.id, isLoadingHistory, dispatch]);

  useEffect(() => {
    setIsNewChat(!isItemsLoading && !(Array.isArray(items) && items.length > 0) && openModal);
  }, [items, openModal, isItemsLoading])

  // Calculate chat started label based on first message
  useEffect(() => {
    if (!items || items.length === 0) {
      setChatStartedLabel("");
      return;
    }

    // Find the earliest message
    const sortedItems = [...items].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
    const firstMessage = sortedItems[0];

    if (firstMessage && firstMessage.createdOn) {
      const startMoment = moment(firstMessage.createdOn);
      const isToday = startMoment.isSame(moment(), 'day');

      if (isToday) {
        setChatStartedLabel(`${messages["chat_startedAt"]} ${startMoment.format('h:mm a').replace('am', 'a.m.').replace('pm', 'p.m.')}`);
      } else {
        setChatStartedLabel(`${messages["chat_startedOn"]} ${startMoment.format('MMMM D, YYYY')}`);
      }
    }
  }, [items, messages]);

  // Memoize first message date to avoid re-sorting on every render
  const firstMessageDate = useMemo(() => {
    if (!items || items.length === 0) return null;
    const sortedItems = [...items].sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
    return sortedItems[0]?.createdOn ? moment(sortedItems[0].createdOn).local().format("YYYY-MM-DD") : null;
  }, [items]);

  useEffect(() => {
    if (!items || !openModal || !onUpdateMessageStatus) return;

    const unread = items.filter((item) => item.receiverEntityNameId === authId && item.status !== "Read");
    if (unread.length > 0 && onUpdateMessageStatus) {
      onUpdateMessageStatus(unread.map((item) => item.id), "Read");
    }

    const grouped = items.reduce((acc, item) => {
      const localDate = moment(item?.createdOn).local().format("YYYY-MM-DD");
      if (!acc[localDate]) {
        acc[localDate] = [];
      }
      acc[localDate].push(item);
      return acc;
    }, {});

    setItemsGrouped(grouped);
  }, [items, openModal, authId]);

  // Scroll to bottom when initial messages load or new message is added
  useEffect(() => {
    // prevent auto scrolling to bottom if user is loading history from above
    if (scrollRef.current && !isLoadingHistory) {
      scrollRef.current.scrollTop = 0;
    }

    if (isLoadingHistory) {
      setIsLoadingHistory(false);
    }
  }, [itemsGrouped]);

  useEffect(() => {
    if (!scrollRef.current || !isLoadingHistory) return; // only attach listener during loading history

    const handleScroll = () => {
      // prevent user from scrolling to the history trigger area when loading
      if (isLoadingHistory && (oldScrollTop - scrollRef.current.scrollTop) >= 0) {
        scrollRef.current.scrollTop = oldScrollTop + 10;
      }
    };

    scrollRef.current.addEventListener('scroll', handleScroll);

    return () => {
      scrollRef.current.removeEventListener('scroll', handleScroll); // clean up
    };
  }, [isLoadingHistory]);

  // Memoize event handlers to prevent recreation
  const handleSendMessage = useCallback(() => {
    if (!draft.trim() || !activePartner || !activeChat.id) return;

    const payload = {
      chatId: activeChat.id,
      transmitterEntityNameId: authId,
      receiverEntityNameId: activePartner.authId,
      message: draft.trim(),
      clientMessageId: `client-${Date.now()}`,
      nextToken,
    };

    onSendMessage?.(payload);

    dispatch(
      chatListUpdateLastMessage({
        chatId: activeChat.id,
        lastMessage: {
          message: draft.trim(),
          transmitterEntityNameId: authId,
          receiverEntityNameId: activePartner.authId,
          status: "Sent",
          createdOn: new Date().toISOString(), // Use ISO string format for moment.js compatibility
        },
      })
    );

    setDraft("");
  }, [draft, activePartner, activeChat.id, authId, onSendMessage, dispatch]);

  const isDisableSendMessage = () => {
    return !draft?.trim() || isItemsLoading || draft?.length > INPUT_MAX_LENGTH;
  }

  const isDisableChatBox = () => {
    return loading;
  }

  const handleClose = useCallback(() => {
    onCloseThread?.();
    resetComponentState();
    dispatch(closeChatsContainer());
    dispatch(resetChatThread());
  }, [onCloseThread, dispatch]);

  const handleBack = useCallback(() => {
    onCloseThread?.();
    resetComponentState();
    dispatch(resetChatThread());
  }, [onCloseThread, dispatch]);

  const handleExpand = useCallback(() => {
    dispatch(setChatsContainerIsExpanded(!isExpanded));
  }, [dispatch, isExpanded]);

  const resetComponentState = () => {
    setDraft("");
    setItemsGrouped({});
    setOldScrollTop(0);
    setIsNewChat(false);
  }

  // Calculate and set the outer height (header + footer + margins) to adjust scroll area
  const calcOuterHeight = () => {
    if (!inputRef.current || !scrollRef.current) return;
    const inputHeight = inputRef.current.getBoundingClientRect().height;
    let inputContainerHeight; // without the input height
    if (Math.round(inputHeight / defaultInputHeight) > 1) { // calc lines number textarea have
      setShowInputCharacterCounter(true);
      inputContainerHeight = 50; // add character counter height
    } else {
      setShowInputCharacterCounter(false);
      inputContainerHeight = 34;
    }
    const adjustedHeight = defaultOuterHeight + inputContainerHeight + inputHeight + 6; // add small look-good height

    scrollRef.current.style.maxHeight = `calc(100vh - ${adjustedHeight}px)`;
    scrollRef.current.style.height = `calc(100vh - ${adjustedHeight}px)`;
  };

  // Observe input height changes to recalculate scroll area
  useEffect(() => {
    if (openModal) {
      recalculate();
    }
  }, [openModal, showInputCharacterCounter, recalculate]);

  // Update language when change locale.
  useEffect(() => {
    moment.locale(locale);
    const dateLocale = {
      ...DATE_LABELS,
      sameDay: `[${messages["date_today"]}]`,
      lastDay: `[${messages["date_yesterday"]}]`,
      lastWeek: `[${messages["last_day"]}] dddd`,
    }
    setDateLocaleLabel(dateLocale)
  }, [locale, messages])

  return (
    openModal && (
      <Modal
        className={cx("chat-thread", `chat-type-${chatType}`)}
        title={activePartner?.label || activeChat?.name}
        open={openModal}
        size="small"
        position="right"
        handleClose={handleClose}
        useExpandMore
        handleExpand={handleExpand}
        iconBackClose
        handleBack={handleBack}
        isSkeletonLoading={true}
      >
        {
          (activePartner?.label || activeChat?.name) &&
          <div className="chat-user-status">
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              wrap="nowrap"
              spacing={2}
              style={{ width: '0' }}
            >
              <Grid item sm={10} style={{ paddingLeft: 0 }}>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, marginLeft: 35 }} >{chatUserStatus}</Typography>
              </Grid>
            </Grid>

          </div>
        }
        <ModalBody className="chat-thread-body">
          {((loading && !hasMessages) || isLoadingHistory) && <LinearProgress color="primary" />}
          <div ref={scrollRef} id="chat-thread-scroll" style={{ flex: isNewChat ? 'unset' : '1 1 0%' }} className="chat-thread-messages scrollbar-style">
            <InfiniteScroll
              scrollThreshold={"20px"}
              dataLength={items?.length || 0}
              inverse={true}
              hasMore={nextToken !== null && !isLoadingHistory}
              loader={<div />}
              scrollableTarget="chat-thread-scroll"
              className="chat-thread-scroller"
              next={() => fetchMoreMessages()}
            >
              {showInitialSkeleton && <SkeletonChatMessages />}
              {chatStartedLabel && hasMessages && nextToken === null && (
                <div className="chat-thread-started">
                  <span
                    className="chat-thread-system-label"
                  >{chatStartedLabel}</span>
                </div>
              )}
              {Object.keys(itemsGrouped || {})
                .sort((a, b) => new Date(a) - new Date(b))
                .map((groupKey) => {
                  const dateLabel = moment(groupKey).calendar(dateLocaleLabel);
                  const messagesForDay = [...itemsGrouped[groupKey]].sort(
                    (a, b) => new Date(a.createdOn) - new Date(b.createdOn)
                  );

                  // Use memoized first message date
                  const shouldHideDatePill = firstMessageDate === groupKey;

                  return (
                    <div key={`date-group-${groupKey}`} className="chat-thread-day">
                      {!shouldHideDatePill && (
                        <span
                          className="chat-thread-system-label"
                          key={`date-pill-${groupKey}`}
                        >{dateLabel}</span>
                      )}
                      {messagesForDay.map((row, index) => (
                        <MessageBubble
                          key={`message-${row.id || index}`}
                          message={row}
                          chatUsers={chatUsers}
                          sender={row?.transmitterEntityNameId === authId ? "self" : "bot"}
                          chatType={chatType}
                        />
                      ))}
                    </div>
                  );
                })}

            </InfiniteScroll>
            {isNewChat && <span className="chat-thread-system-label">{messages["chat_startNewConversation"]}</span>}
          </div>
        </ModalBody>

        <ModalFooter className="chat-thread-footer">
          <div className="chat-composer">
            <div
              className={`chat-composer-input-container ${showInputCharacterCounter ? 'multiline' : 'oneline'} ${isDisableChatBox() ? 'disabled' : ''}`}
            >
              <InputBase
                className="chat-composer-input scrollbar-style"
                placeholder={messages["FSM_SETTINGS_MESSAGES_WRITE_MESSAGE"]}
                multiline
                minRows={1}
                maxRows={6}
                ref={inputRef}
                value={draft}
                disabled={isDisableChatBox()}
                autoFocus
                onKeyDown={(event) => {
                  if (event.key !== "Enter" || isDisableSendMessage()) return;
                  event.preventDefault();
                  handleSendMessage();
                }}
                onClick={() => {
                  if (activeChat.id) {
                    dispatch(setChatUserWriting({ chatId: activeChat.id, userId: authId }));
                  }
                }}
                onChange={(e) => setDraft(e.target.value)}
              />
              {showInputCharacterCounter && <Typography
                className={`length-indicator ${draft.length > INPUT_MAX_LENGTH ? 'error' : ''}`}
                variant="body2"
                color="textSecondary"
              >{draft.length}/{INPUT_MAX_LENGTH}</Typography>}
            </div>
            <IconButton
              className={cx("chat-composer-submit", `chat-type-${chatType}`)}
              aria-label="submit"
              disabled={isDisableSendMessage()}
              onClick={handleSendMessage}
            >
              <NearMeOutlined />
            </IconButton>
          </div>
        </ModalFooter>
      </Modal>
    )
  );
};

export default ChatThread;
